export class NodeOnboardingConnectDiscoveryElement extends HTMLElement {
    #state = { steps: [] };
    set state(value) {
        this.#state = value;
        this.#render();
    }
    get state() {
        return this.#state;
    }
    connectedCallback() {
        this.#render();
    }
    #render() {
        const root = this.shadowRoot ?? this.attachShadow({ mode: "open" });
        root.replaceChildren();
        const style = document.createElement("style");
        style.textContent = `
      :host { display: block; font-family: var(--ui-font, Inter, system-ui, sans-serif); color: var(--ink-hi, #0A0A0A); }
      section {
        display: flex; flex-direction: column; gap: 16px;
        padding: clamp(20px, 6vw, 36px) clamp(16px, 5vw, 32px);
        background: var(--bg-raised, #FFFFFF);
        border: 1px solid var(--ui-line-soft, rgba(0, 0, 0, 0.06));
        border-radius: var(--ui-radius-lg, 14px);
        box-shadow: var(--ui-shadow-1, 0 1px 1px rgba(10, 10, 10, 0.05), 0 10px 24px -14px rgba(10, 10, 10, 0.25));
      }
      .eyebrow {
        font: 600 10px/1 var(--ui-mono, "JetBrains Mono", ui-monospace, monospace);
        letter-spacing: 0.14em; text-transform: uppercase;
        color: var(--ink-low, #888888);
        padding-bottom: 12px; border-bottom: 1px solid var(--hr, rgba(10, 10, 10, 0.07));
      }
      h1 {
        margin: 0; font: 400 clamp(1.75rem, 6vw, 2.125rem)/1.05 var(--ui-serif, "Instrument Serif", serif);
      }
      p.body { margin: 0; font-size: 13.5px; line-height: 1.6; color: var(--ink-mid, #545454); }
      .card {
        display: flex; flex-direction: column; gap: 12px;
        padding: 14px; border: 1px solid var(--hr-strong, rgba(10, 10, 10, 0.12)); border-radius: 10px;
      }
      .card-row { display: flex; align-items: center; gap: 12px; }
      .avatar {
        display: flex; align-items: center; justify-content: center; flex-shrink: 0;
        width: 30px; height: 30px; border-radius: 10px; background: var(--c-peri, #C0CAE8);
        font: 600 12px/1 var(--ui-mono, "JetBrains Mono", ui-monospace, monospace); color: var(--ink-hi, #0A0A0A);
      }
      .identity { display: flex; flex-direction: column; gap: 2px; min-width: 0; }
      .name { font-size: 13px; font-weight: 600; }
      .status {
        font: 400 9.5px/1 var(--ui-mono, "JetBrains Mono", ui-monospace, monospace);
        letter-spacing: 0.06em; text-transform: uppercase;
        color: var(--ink-low, #888888);
      }
      .set-up {
        min-height: 2.5rem; border: 1px solid var(--ink-hi, #0A0A0A); border-radius: var(--ui-radius, 10px);
        padding: .6rem 1rem; font: 500 .875rem/1.2 var(--ui-font, Inter, system-ui, sans-serif);
        color: var(--bg-0, #FFFFFF); background: var(--ink-hi, #0A0A0A); cursor: pointer;
        transition: background 0.18s var(--ease-out, ease-out), border-color 0.18s var(--ease-out, ease-out);
      }
      .set-up:hover { background: var(--c-coral-deep, #B85551); border-color: var(--c-coral-deep, #B85551); }
      .set-up:focus-visible {
        outline: 2px solid var(--ds-accent, #D4635F); outline-offset: 2px;
      }
      .manual-entry {
        display: flex; flex-direction: column; gap: 8px;
        padding: 12px; border: 1px dashed var(--hr-strong, rgba(10, 10, 10, 0.12)); border-radius: 10px;
      }
      .manual-entry-label {
        font: 400 9.5px/1 var(--ui-mono, "JetBrains Mono", ui-monospace, monospace);
        letter-spacing: 0.06em; text-transform: uppercase;
        color: var(--ink-low, #888888);
      }
      .manual-entry-row { display: flex; gap: 8px; }
      .manual-entry-input {
        flex: 1; min-width: 0; min-height: 2.25rem; padding: 0 .6rem;
        font: 400 13px/1.2 var(--ui-font, Inter, system-ui, sans-serif);
        color: var(--ink-hi, #0A0A0A); background: var(--bg-0, #FFFFFF);
        border: 1px solid var(--hr-strong, rgba(10, 10, 10, 0.12)); border-radius: var(--ui-radius, 10px);
      }
      .manual-entry-input:focus-visible {
        outline: 2px solid var(--ds-accent, #D4635F); outline-offset: 2px;
      }
      .manual-entry-submit {
        min-height: 2.25rem; padding: 0 .9rem; white-space: nowrap;
        font: 500 .8125rem/1.2 var(--ui-font, Inter, system-ui, sans-serif);
        color: var(--ink-hi, #0A0A0A); background: var(--bg-raised, #FFFFFF);
        border: 1px solid var(--ink-hi, #0A0A0A); border-radius: var(--ui-radius, 10px);
        cursor: pointer;
      }
      .manual-entry-submit:hover { background: var(--bg-1, #F5F5F5); }
      .manual-entry-submit:focus-visible {
        outline: 2px solid var(--ds-accent, #D4635F); outline-offset: 2px;
      }
      .manual-entry-submit:disabled { cursor: default; opacity: 0.6; }
      .manual-status {
        display: flex; flex-direction: column; gap: 8px;
        font-size: 12.5px; line-height: 1.5; color: var(--ink-mid, #545454);
      }
      .manual-status--probing { flex-direction: row; align-items: center; gap: 8px; }
      .probing-dot {
        display: inline-block; flex-shrink: 0; width: 8px; height: 8px; border-radius: 999px;
        background: var(--c-coral, #DF6662);
        animation: node-discovery-pulse 1.6s var(--ease-out, cubic-bezier(0.2, 0.9, 0.2, 1)) infinite;
      }
      .manual-found { border-style: solid; }
      .manual-retry {
        align-self: flex-start;
      }
      .cert-guidance {
        display: flex; flex-direction: column; gap: 8px;
        padding: 12px; border: 1px solid var(--hr-strong, rgba(10, 10, 10, 0.12)); border-radius: 10px;
        background: var(--bg-1, #F5F5F5);
      }
      .cert-guidance-heading { margin: 0; font-size: 13px; font-weight: 600; color: var(--ink-hi, #0A0A0A); }
      .cert-guidance-body { margin: 0; font-size: 12.5px; line-height: 1.5; color: var(--ink-mid, #545454); }
      .cert-guidance-subhead {
        margin: 4px 0 0; font: 600 9.5px/1 var(--ui-mono, "JetBrains Mono", ui-monospace, monospace);
        letter-spacing: 0.08em; text-transform: uppercase; color: var(--ink-low, #888888);
      }
      .cert-guidance-steps {
        margin: 0; padding-left: 18px; display: flex; flex-direction: column; gap: 4px;
        font-size: 12.5px; line-height: 1.5; color: var(--ink-mid, #545454);
      }
      .cert-open-link {
        align-self: flex-start; font-size: 12.5px; font-weight: 500;
        color: var(--ds-accent, #D4635F); text-decoration: underline;
      }
      .cert-open-link:focus-visible {
        outline: 2px solid var(--ds-accent, #D4635F); outline-offset: 2px;
      }
      @keyframes node-discovery-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
      @media (prefers-reduced-motion: reduce) {
        * { scroll-behavior: auto !important; transition-duration: 0.01ms !important; animation: none !important; }
      }
      @media (forced-colors: active) {
        section, .card, .set-up { border: 1px solid CanvasText; }
        .manual-entry, .manual-entry-input, .manual-entry-submit { border: 1px solid CanvasText; }
        .cert-guidance { border: 1px solid CanvasText; }
        .probing-dot { background: CanvasText; }
      }
    `;
        const section = document.createElement("section");
        const eyebrow = document.createElement("p");
        eyebrow.className = "eyebrow";
        eyebrow.textContent = "Connect";
        const heading = document.createElement("h1");
        heading.textContent = "Connect to your node";
        const body = document.createElement("p");
        body.className = "body";
        body.textContent = "Enter your node’s address on this network — its IP, .local name, or https://…";
        const status = document.createElement("div");
        status.setAttribute("aria-live", "polite");
        const { candidate } = this.#state;
        if (candidate === undefined) {
            status.append(this.#renderManualEntry());
        }
        else {
            status.append(this.#renderCard(candidate));
        }
        section.append(eyebrow, heading, body, status);
        root.append(style, section);
    }
    #renderCard(candidate) {
        const card = document.createElement("div");
        card.className = "card";
        const row = document.createElement("div");
        row.className = "card-row";
        const avatar = document.createElement("span");
        avatar.className = "avatar";
        avatar.setAttribute("aria-hidden", "true");
        avatar.textContent = candidate.displayName.charAt(0);
        const identity = document.createElement("div");
        identity.className = "identity";
        const name = document.createElement("span");
        name.className = "name";
        name.textContent = candidate.displayName;
        const statusLine = document.createElement("span");
        statusLine.className = "status";
        statusLine.textContent = `${candidate.maskedId} · ${candidate.statusLabel}`;
        identity.append(name, statusLine);
        row.append(avatar, identity);
        const setUp = document.createElement("button");
        setUp.type = "button";
        setUp.className = "set-up";
        setUp.textContent = "Set up this node";
        setUp.addEventListener("click", () => {
            this.dispatchEvent(new CustomEvent("set-up", { bubbles: true, composed: true }));
        });
        card.append(row, setUp);
        return card;
    }
    /**
     * The lan-direct-pairing manual-address affordance: without a signed bootstrap descriptor
     * there is no automatic LAN candidate source, so the only way to reach one is to type it.
     * Submitting (or a Retry button inside `#renderManualOutcome`) emits `discover-node` — this
     * element never touches the network itself, that's the kernel's job via the RPC seam the
     * gate drives. The address input persists across re-renders (`manual.address` seeds its
     * value) so a Retry after a failed probe reuses exactly what the person typed.
     */
    #renderManualEntry() {
        const wrapper = document.createElement("div");
        wrapper.className = "manual-entry";
        const label = document.createElement("label");
        label.className = "manual-entry-label";
        label.htmlFor = "node-address";
        label.textContent = "Enter your node's address";
        const manual = this.#state.manual;
        const probing = manual?.status === "probing";
        const form = document.createElement("form");
        form.className = "manual-entry-row";
        const input = document.createElement("input");
        input.type = "text";
        input.name = "node-address";
        input.id = "node-address";
        input.className = "manual-entry-input";
        input.placeholder = "192.168.1.x, mynode.local, or https://…";
        input.autocomplete = "off";
        input.spellcheck = false;
        if (manual !== undefined)
            input.value = manual.address;
        const submit = document.createElement("button");
        submit.type = "submit";
        submit.className = "manual-entry-submit";
        submit.dataset.action = "discover-node";
        submit.textContent = probing ? "Searching…" : "Find node";
        submit.disabled = probing;
        form.addEventListener("submit", (event) => {
            event.preventDefault();
            this.#submitAddress(input.value);
        });
        form.append(input, submit);
        wrapper.append(label, form);
        const outcome = manual === undefined ? null : this.#renderManualOutcome(manual);
        if (outcome !== null)
            wrapper.append(outcome);
        return wrapper;
    }
    #submitAddress(rawAddress) {
        const address = rawAddress.trim();
        if (address.length === 0)
            return;
        this.dispatchEvent(new CustomEvent("discover-node", {
            bubbles: true,
            composed: true,
            detail: { address },
        }));
    }
    #renderManualOutcome(manual) {
        switch (manual.status) {
            case "probing":
                return this.#renderManualProbing(manual.address);
            case "verified":
                return manual.candidate === undefined ? null : this.#renderManualVerified(manual.candidate);
            case "reached-but-invalid":
                return this.#renderManualInvalid(manual.address, manual.reason ?? "it isn't your node");
            case "unreachable-or-untrusted":
                // A publicly-trusted origin has no cert wall to guide the user around — the browser
                // just fetches it — so once `trustMode` has been proven `"publicly-trusted"` for this
                // address (Tier-0 trusted-IPv6 e2e plan, Task 2), a later failure here is a real,
                // transient error, not an untrusted-certificate rejection.
                return manual.trustMode === "publicly-trusted"
                    ? this.#renderManualTransientError(manual.address)
                    : this.#renderManualUntrusted(manual.address);
        }
    }
    #renderManualProbing(address) {
        const panel = document.createElement("div");
        panel.className = "manual-status manual-status--probing";
        const dot = document.createElement("span");
        dot.className = "probing-dot";
        dot.setAttribute("aria-hidden", "true");
        const text = document.createElement("span");
        text.textContent = `Checking ${address} …`;
        panel.append(dot, text);
        return panel;
    }
    /**
     * The manual flow's "found your node" outcome. Unlike the auto-discovered `#renderCard`
     * (whose "Set up this node" advances the onboarding scenario), "Continue" here only emits
     * `node-discovered` carrying the wire-safe `NodeDiscoveryCandidate` — setting it as the
     * home node is deliberately out of scope for this element (lan-direct-pairing plan, C3).
     */
    #renderManualVerified(candidate) {
        const card = document.createElement("div");
        card.className = "card manual-found";
        const row = document.createElement("div");
        row.className = "card-row";
        const avatar = document.createElement("span");
        avatar.className = "avatar";
        avatar.setAttribute("aria-hidden", "true");
        avatar.textContent = candidate.address.charAt(0).toUpperCase();
        const identity = document.createElement("div");
        identity.className = "identity";
        const name = document.createElement("span");
        name.className = "name";
        name.textContent = "Found your node";
        const statusLine = document.createElement("span");
        statusLine.className = "status";
        statusLine.textContent = `${candidate.address} · ${shortNodeId(candidate.id)}`;
        identity.append(name, statusLine);
        row.append(avatar, identity);
        const proceed = document.createElement("button");
        proceed.type = "button";
        proceed.className = "set-up manual-continue";
        proceed.textContent = "Continue";
        proceed.addEventListener("click", () => {
            this.dispatchEvent(new CustomEvent("node-discovered", {
                bubbles: true,
                composed: true,
                detail: { candidate },
            }));
        });
        card.append(row, proceed);
        return card;
    }
    #renderManualInvalid(address, reason) {
        const panel = document.createElement("div");
        panel.className = "manual-status manual-status--invalid";
        const message = document.createElement("p");
        message.style.margin = "0";
        message.textContent = `That address responded but isn’t your node (${reason}).`;
        const retry = document.createElement("button");
        retry.type = "button";
        retry.className = "manual-entry-submit manual-retry";
        retry.textContent = "Retry";
        retry.addEventListener("click", () => this.#submitAddress(address));
        panel.append(message, retry);
        return panel;
    }
    /**
     * The publicly-trusted counterpart to `#renderManualUntrusted` (Tier-0 trusted-IPv6 e2e
     * plan, Task 2): once `trustMode` is known `"publicly-trusted"` for this address, there is
     * no cert wall to blame, so this renders a plain "couldn’t reach it, try again" panel
     * instead of the accept-cert / iPhone-profile guidance below.
     */
    #renderManualTransientError(address) {
        const panel = document.createElement("div");
        panel.className = "manual-status manual-status--invalid";
        const message = document.createElement("p");
        message.style.margin = "0";
        message.textContent = "We couldn’t reach that node just now. Check it’s powered on and try again.";
        const retry = document.createElement("button");
        retry.type = "button";
        retry.className = "manual-entry-submit manual-retry";
        retry.textContent = "Retry";
        retry.addEventListener("click", () => this.#submitAddress(address));
        panel.append(message, retry);
        return panel;
    }
    /**
     * Cert-trust guidance for the `"unreachable-or-untrusted"` outcome when `trustMode` is not
     * known `"publicly-trusted"` for this address — deliberately opaque about *why* (a
     * self-signed-cert rejection and a plain network failure both collapse into this one
     * status, per `NodeDiscoveryResult`'s doc comment), so the copy leads with the most likely,
     * most actionable cause: an untrusted node certificate.
     */
    #renderManualUntrusted(address) {
        const origin = httpsOrigin(address);
        const panel = document.createElement("div");
        panel.className = "cert-guidance";
        const heading = document.createElement("p");
        heading.className = "cert-guidance-heading";
        heading.textContent = "We couldn’t verify that address.";
        const body = document.createElement("p");
        body.className = "cert-guidance-body";
        body.textContent = "Your node likely uses a security certificate your browser doesn’t trust yet.";
        const desktopHeading = document.createElement("p");
        desktopHeading.className = "cert-guidance-subhead";
        desktopHeading.textContent = "On this device";
        const desktopSteps = renderSteps([
            `Open ${origin} in a new tab`,
            "Accept the security warning",
            "Come back here and tap Retry",
        ]);
        const link = document.createElement("a");
        link.className = "cert-open-link";
        link.href = origin;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        link.textContent = `Open ${origin}`;
        const iosHeading = document.createElement("p");
        iosHeading.className = "cert-guidance-subhead";
        iosHeading.textContent = "On iPhone / iPad";
        const iosSteps = renderSteps([
            "Open the link above and install the certificate profile it offers",
            "Settings → General → VPN & Device Management → install the certificate",
            "Settings → General → About → Certificate Trust Settings → turn on full trust for it",
            "Come back here and tap Retry",
        ]);
        const retry = document.createElement("button");
        retry.type = "button";
        retry.className = "manual-entry-submit manual-retry";
        retry.textContent = "Retry";
        retry.addEventListener("click", () => this.#submitAddress(address));
        panel.append(heading, body, desktopHeading, desktopSteps, link, iosHeading, iosSteps, retry);
        return panel;
    }
}
function renderSteps(steps) {
    const list = document.createElement("ol");
    list.className = "cert-guidance-steps";
    for (const step of steps) {
        const item = document.createElement("li");
        item.textContent = step;
        list.append(item);
    }
    return list;
}
/** Best-effort HTTPS origin for the "open this address" guidance link — display only, the
 * kernel's own `discoverNodeByAddress` normalization is what actually matters for the probe
 * itself. Strips any scheme the person typed and always prefers `https://`, since the whole
 * point of this guidance is getting the browser to trust the node's HTTPS certificate. */
function httpsOrigin(address) {
    const trimmed = address.trim();
    return `https://${trimmed.replace(/^https?:\/\//i, "")}`;
}
/** Masks a raw node id down to a short, glanceable form — mirrors the auto-discovered
 * card's `maskedId` shape (e.g. `CN-9F3A····D218`) without assuming that exact prefix
 * convention, since a manually-verified candidate's `id` is the bare wire value. */
function shortNodeId(id) {
    if (id.length <= 12)
        return id;
    return `${id.slice(0, 6)}····${id.slice(-4)}`;
}
//# sourceMappingURL=connect-discovery.js.map